import { useEffect, useMemo, useRef, useState } from 'react';

import { FormikValues, useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  adminActions,
  adminBroadcastGroupsForDropdown,
  adminCreateBroadcastGroupLeads,
  adminGetLead,
  adminLeadFilter,
  getBroadcastGroupsForDropdown,
  getBroadcastRecipients,
  getCreateBroadcastGroupLeadsResponse,
  getExistingUserDetails,
  getLeadsFilter,
  HelpTypes,
  IAdminBroadcastUserGroupApiData,
  ICreateLeadParams,
  leadSelector,
  LeadStatusType,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';

import { BroadcastGroupActionType } from '../../../helpers/Constants';
import strings from '../../../localization';
import { campaignStatusMap } from '../../../components/Admin/Campaigns/helper';
import {
  getEligibleBroadcastGroupMembers,
  LeadCreationMethod,
} from '../../../components/Admin/Leads/CreateEditLead/helper';
import validator from '../../../components/Admin/Leads/CreateEditLead/validator';
import {
  getBroadcastGroupLeadConfirmation,
} from '../../../components/Admin/Leads/CreateEditLead/BroadcastGroupLeadConfirmation';
import {
  getBroadcastGroupLeadsSuccessMessage,
} from '../../../components/Admin/Leads/CreateEditLead/BroadcastGroupLeadsSuccessMessage';
import { showSuccess } from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import { setDialog } from '../../../reducers/dialogReducer';
import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';

import useExistingLeads from './useExistingLeads';

const useCreateEditLead = (type: BroadcastGroupActionType) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();

  const { leadId, userType } = (state ?? {}) as {
    leadId?: string;
    userType?: HelpTypes;
  };
  const isEdit = type === BroadcastGroupActionType.edit;
  const campaignUserType = userType ?? HelpTypes.pharmacy;

  const lead = useAppSelector(leadSelector);
  const leadsFilter = useAppSelector(adminLeadFilter);
  const broadcastGroups = useAppSelector(adminBroadcastGroupsForDropdown);
  const createBroadcastGroupLeadsResponse = useAppSelector(
    getCreateBroadcastGroupLeadsResponse,
  );
  const { campaigns } = leadsFilter ?? {};

  const [payload, setPayload] = useState<ICreateLeadParams>({
    name: '',
    email: '',
    campaignId: null,
    userType: campaignUserType,
    postalCode: '',
    notes: '',
    phone: '',
  });
  const [selectedGroupRecipients, setSelectedGroupRecipients] = useState<Array<IAdminBroadcastUserGroupApiData>>([]);
  const [loadedBroadcastGroupId, setLoadedBroadcastGroupId] = useState('');
  const submittedBroadcastGroupMemberCount = useRef(0);

  const createSuccess = useAppSelector((store) =>
    successSelector([adminActions.createLead], store));
  const updateSuccess = useAppSelector((store) =>
    successSelector([adminActions.updateLead], store));
  const createBroadcastGroupLeadsSuccess = useAppSelector((store) =>
    successSelector([adminActions.createBroadcastGroupLeads], store));

  const campaignOptions = useMemo(
    () =>
      campaigns.map((item) =>
        ({
          label: item.name,
          key: item.campaignId,
          subtitle:
          campaignStatusMap[item?.status as keyof typeof campaignStatusMap]?.label
          ?? strings.expired,
          subtitleStyle: {
            color: campaignStatusMap[item?.status as keyof typeof campaignStatusMap]?.color,
          },
        })),
    [campaigns],
  );

  const broadcastGroupOptions = useMemo(
    () =>
      (broadcastGroups?.data ?? []).map((item) =>
        ({
          key: item.broadcastGroupId,
          label: item.name,
        })),
    [broadcastGroups],
  );

  const initialValues = useMemo(() => {
    if (isEdit) {
      return {
        name: lead?.name || '',
        email: lead?.email || '',
        campaignId: lead?.campaignId || '',
        userType: campaignUserType,
        creationMethod: LeadCreationMethod.email,
        broadcastGroupId: lead?.broadcastGroupId || '',
        emails: [] as Array<string>,
        removedEmails: [] as Array<string>,
        sendInviteAt: lead?.sendInviteAt ?? 0,
        lastInviteSentAt: lead?.lastInviteSentAt || '',
        status: lead?.status ?? LeadStatusType.Pending,
        notes: lead?.notes || '',
        postalCode: lead?.postalCode || '',
        phone: lead?.phone,
      };
    }

    return {
      name: '',
      email: '',
      campaignId: '',
      userType: campaignUserType,
      creationMethod: LeadCreationMethod.email,
      broadcastGroupId: '',
      emails: [] as Array<string>,
      removedEmails: [] as Array<string>,
      sendInviteAt: 0,
      lastInviteSentAt: '',
      status: LeadStatusType.Pending,
      notes: '',
      phone: '',
      postalCode: '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead]);

  const createBroadcastGroupLeads = (values: FormikValues) => {
    submittedBroadcastGroupMemberCount.current = values.emails.length;
    dispatch(
      adminCreateBroadcastGroupLeads({
        campaignId: values.campaignId,
        broadcastGroupId: values.broadcastGroupId,
        notes: values.notes || '',
        emailsToRemove: values.removedEmails ?? [],
      }),
    );
  };

  const handleSubmit = (values: FormikValues) => {
    if (values.creationMethod === LeadCreationMethod.broadcastGroup) {
      if (!values.emails?.length) {
        return;
      }

      dispatch(
        setDialog({
          Component: getBroadcastGroupLeadConfirmation(() =>
            createBroadcastGroupLeads(values)),
          heading: {
            title: '',
          },
          showCloseButton: false,
        }),
      );
      return;
    }

    const leadPayload: ICreateLeadParams = {
      name: values.name,
      email: values.email,
      campaignId: values.campaignId || null,
      userType: campaignUserType,
      notes: values.notes || '',
      phone: values?.phone,
      postalCode: values?.postalCode,
      broadcastGroupId: values.broadcastGroupId || null,
    };

    setPayload(leadPayload);
    dispatch(getExistingUserDetails(values?.email ?? ''));
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validationSchema: validator(),
  });
  const selectedBroadcastGroupId = formik.values.broadcastGroupId;
  const setLeadFieldValue = formik.setFieldValue;

  const isBroadcastGroupCreation = !isEdit
    && formik.values.creationMethod === LeadCreationMethod.broadcastGroup;
  const eligibleBroadcastGroupMembers = useMemo(
    () =>
      getEligibleBroadcastGroupMembers(
        selectedGroupRecipients,
        campaignUserType,
      ),
    [campaignUserType, selectedGroupRecipients],
  );
  const eligibleEmails = useMemo(
    () =>
      eligibleBroadcastGroupMembers.map((member) =>
        member.email),
    [eligibleBroadcastGroupMembers],
  );
  const visibleEmails = useMemo(() => {
    const removedEmails = new Set(
      formik.values.removedEmails.map((email) =>
        email.toLowerCase()),
    );

    return eligibleEmails.filter((email) =>
      !removedEmails.has(email.toLowerCase()));
  }, [eligibleEmails, formik.values.removedEmails]);
  const cannotMapBroadcastGroup = isBroadcastGroupCreation
    && !!formik.values.broadcastGroupId
    && loadedBroadcastGroupId === formik.values.broadcastGroupId
    && !eligibleBroadcastGroupMembers.length;

  useExistingLeads({
    payload,
    isEdit,
    leadId: leadId ?? '',
    enabled: !isBroadcastGroupCreation,
  });

  useEffect(() => {
    dispatch(
      getLeadsFilter({
        userType: campaignUserType,
        dontShowExpired: !isEdit,
      }),
    );

    if (isEdit && leadId) {
      dispatch(adminGetLead(leadId));
    } else {
      dispatch(getBroadcastGroupsForDropdown());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isEdit, leadId]);

  useEffect(() => {
    let isCurrentSelection = true;

    if (isBroadcastGroupCreation && selectedBroadcastGroupId) {
      setSelectedGroupRecipients([]);
      setLoadedBroadcastGroupId('');
      setLeadFieldValue('emails', [], false);
      setLeadFieldValue('removedEmails', [], false);
      dispatch(getBroadcastRecipients([selectedBroadcastGroupId])).then((result) => {
        if (isCurrentSelection && getBroadcastRecipients.fulfilled.match(result)) {
          setSelectedGroupRecipients(result.payload ?? []);
          setLoadedBroadcastGroupId(selectedBroadcastGroupId);
        }
      });
    } else {
      setSelectedGroupRecipients([]);
      setLoadedBroadcastGroupId('');
      setLeadFieldValue('emails', [], false);
      setLeadFieldValue('removedEmails', [], false);
    }

    return () => {
      isCurrentSelection = false;
    };
  }, [
    dispatch,
    isBroadcastGroupCreation,
    selectedBroadcastGroupId,
    setLeadFieldValue,
  ]);

  useEffect(() => {
    const currentEmails = formik.values.emails.join('\n');
    const nextEmails = visibleEmails.join('\n');

    if (isBroadcastGroupCreation && currentEmails !== nextEmails) {
      formik.setFieldValue('emails', visibleEmails, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBroadcastGroupCreation, visibleEmails]);

  const handleRemoveEmail = (emailToRemove: string) => {
    formik.setFieldValue(
      'emails',
      formik.values.emails.filter((email) =>
        email !== emailToRemove),
      false,
    );
    formik.setFieldValue(
      'removedEmails',
      [...formik.values.removedEmails, emailToRemove],
      false,
    );
  };

  useEffect(() => {
    if (createSuccess && !isBroadcastGroupCreation) {
      showSuccess(dispatch, strings.actionCompletedSuccessfully);
      dispatch(resetStatus([adminActions.createLead]));
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSuccess]);

  useEffect(() => {
    if (createBroadcastGroupLeadsSuccess) {
      showSuccess(
        dispatch,
        getBroadcastGroupLeadsSuccessMessage(
          createBroadcastGroupLeadsResponse?.existingLeads ?? [],
          submittedBroadcastGroupMemberCount.current,
        ),
      );
      dispatch(resetStatus([adminActions.createBroadcastGroupLeads]));
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createBroadcastGroupLeadsSuccess]);

  useEffect(() => {
    if (updateSuccess) {
      showSuccess(dispatch, strings.actionCompletedSuccessfully);
      dispatch(resetStatus([adminActions.updateLead]));
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSuccess]);

  return {
    broadcastGroupOptions,
    campaignOptions,
    campaignUserType,
    cannotMapBroadcastGroup,
    formik,
    handleRemoveEmail,
    isBroadcastGroupCreation,
    isEdit,
  };
};

export default useCreateEditLead;
